/*
 *
 *  * Copyright (c) Funtip GbR 2018.
 *  * Simon Oldeboershuis, Perry Prust
 *
 */

import { push } from '@lagunovsky/redux-react-router';
import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Action } from 'typescript-fsa';
import { Album } from '../../../services/playlist';
import { fetchWrapperFunc, fetchWrapperFuncNoLoading, logError } from '../../../utils';
import { handleFetchException, systemFinishFetching, systemStartFetching } from '../../System/actions';
import { fetchFunction, getImage2Api, getPlaylistAdminApi, getSystemConfig } from '../../System/systemConfig';
import { playlistSearchPath } from '../PlaylistSearch';
import {
    ALBUM_ATTACH_COVER_ART_IMAGE,
    ALBUM_ATTACH_MUSICBRAINZ_COVER_ART,
    ALBUM_DELETE,
    ALBUM_DELETE_COVER,
    ALBUM_IMPORT_MUSICBRAINZ_RELEASE,
    ALBUM_LOAD,
    ALBUM_LOAD_MUSICBRAINZ,
    ALBUM_LOAD_MUSICBRAINZ_DETAILS,
    ALBUM_REMOVE_DUPLICATED_SONGS,
    ALBUM_SAVE,
    ALBUM_UPLOAD_IMAGE,
    albumSetMusicbrainz,
    albumSetMusicbrainzDetails,
    albumSetMusicbrainzLoading,
    loadAlbum,
    setAlbum,
    setAlbumSongs,
    setAlbumUploadedImageId,
} from './actions';
import { albumEditAlbumSelector } from './selectors';

const playlistApi = getPlaylistAdminApi();

function* loadAlbumGenerator(action: Action<string>) {
    let albumId = action.payload;
    yield* fetchWrapperFunc(() => playlistApi.getAlbum(albumId), [(r) => setAlbum(r)]);

    yield* fetchWrapperFunc(() => playlistApi.getAlbumSongs(albumId), [(r) => setAlbumSongs(r)]);
}

export async function uploadImage(file: File): Promise<string | undefined> {
    const apiUrl = getSystemConfig().imageApiUrl;

    try {
        const formData = new FormData();
        formData.append('file', file);

        const params = Object.assign({}, { method: 'POST', body: formData });
        let imageId = await fetchFunction(apiUrl + '/image', params).then((response) => {
            return response.text();
        });
        return imageId;
    } catch (err) {
        throw err;
    }
}

export function* uploadImageGenerator(file: File) {
    let imageId: string | undefined = undefined;
    try {
        yield put(systemStartFetching());
        imageId = yield uploadImage(file);
    } catch (err) {
        logError(err);
        if (err instanceof Response) {
            yield put(handleFetchException(err));
        }
    } finally {
        yield put(systemFinishFetching());
    }
    return imageId;
}

function* uploadAlbumImageGenerator(action: Action<File>) {
    let file = action.payload;
    yield select(albumEditAlbumSelector);

    const imageId = yield* uploadImageGenerator(file);
    if (imageId) {
        yield put(setAlbumUploadedImageId(imageId));
    }
}

function* saveAlbumGenerator(action: Action<Album>) {
    let album = action.payload;

    yield* fetchWrapperFunc(() => playlistApi.updateAlbum(album), [(r) => setAlbum(r)]);
}

function* loadMusicbrainz(action: Action<string>) {
    yield put(albumSetMusicbrainzLoading(true));
    try {
        yield* fetchWrapperFuncNoLoading(
            () => playlistApi.getMusicbrainzAlbumList(action.payload),
            [(r) => albumSetMusicbrainz(r)],
        );
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* loadMusicbrainzDetails(action: Action<string>) {
    yield put(albumSetMusicbrainzLoading(true));
    try {
        yield* fetchWrapperFuncNoLoading(
            () => playlistApi.getMusicbrainzRelease(action.payload),
            [(r) => albumSetMusicbrainzDetails(r)],
        );
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* deleteCover(action: Action<string>) {
    yield put(albumSetMusicbrainzLoading(true));
    try {
        yield* fetchWrapperFuncNoLoading(() => playlistApi.deleteAlbumCover(action.payload), [(r) => setAlbum(r)]);
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* deleteAlbum(action: Action<string>) {
    yield put(albumSetMusicbrainzLoading(true));
    try {
        yield* fetchWrapperFuncNoLoading(
            () => playlistApi.deleteAlbum(action.payload),
            [(r) => push(playlistSearchPath())],
        );
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* removeDuplicatedSongs(action: Action<string>) {
    const albumId = action.payload;
    yield put(albumSetMusicbrainzLoading(true));
    try {
        yield* fetchWrapperFuncNoLoading(() => playlistApi.removeDuplicatedSongs(albumId), [(r) => setAlbum(r)]);
        yield put(loadAlbum(albumId));
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* attachMusicbrainzCoverArt(action: Action<{ albumId: string; url: string }>) {
    const albumId = action.payload.albumId;
    const url = action.payload.url;
    yield put(albumSetMusicbrainzLoading(true));

    const imageApi = getImage2Api();
    try {
        const imageId = yield* fetchWrapperFuncNoLoading(() => imageApi.createExternalImage({uri: url}));
        if (imageId) {
            yield* fetchWrapperFuncNoLoading(() => playlistApi.assignCoverArt(albumId, imageId));
            yield put(loadAlbum(albumId));
        }
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* attachCoverArtImage(action: Action<{ albumId: string; imageId: string }>) {
    const albumId = action.payload.albumId;
    const imageId = action.payload.imageId;
    yield put(albumSetMusicbrainzLoading(true));

    try {
        if (imageId) {
            yield* fetchWrapperFuncNoLoading(() => playlistApi.assignCoverArt(albumId, imageId));
            yield put(loadAlbum(albumId));
        }
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* importMusicbrainzRelease(action: Action<{ albumId: string; releaseId: string }>) {
    const albumId = action.payload.albumId;
    const releaseId = action.payload.releaseId;
    yield put(albumSetMusicbrainzLoading(true));

    try {
        yield* fetchWrapperFuncNoLoading(() => playlistApi.importMusicbrainzRelease(albumId, releaseId));
        yield put(loadAlbum(albumId));
    } finally {
        yield put(albumSetMusicbrainzLoading(false));
    }
}

function* watchForAlbumEditActions() {
    yield takeEvery(ALBUM_LOAD, loadAlbumGenerator);
    yield takeLatest(ALBUM_UPLOAD_IMAGE, uploadAlbumImageGenerator);
    yield takeLatest(ALBUM_SAVE, saveAlbumGenerator);
    yield takeLatest(ALBUM_LOAD_MUSICBRAINZ, loadMusicbrainz);
    yield takeEvery(ALBUM_LOAD_MUSICBRAINZ_DETAILS, loadMusicbrainzDetails);
    yield takeLatest(ALBUM_DELETE_COVER, deleteCover);
    yield takeLatest(ALBUM_DELETE, deleteAlbum);
    yield takeLatest(ALBUM_REMOVE_DUPLICATED_SONGS, removeDuplicatedSongs);
    yield takeLatest(ALBUM_ATTACH_MUSICBRAINZ_COVER_ART, attachMusicbrainzCoverArt);
    yield takeLatest(ALBUM_IMPORT_MUSICBRAINZ_RELEASE, importMusicbrainzRelease);
    yield takeLatest(ALBUM_ATTACH_COVER_ART_IMAGE, attachCoverArtImage);
}

export function* albumEditSagas() {
    yield all([watchForAlbumEditActions()]);
}
